import { useCallback, useEffect, useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { uniqBy } from "lodash";
import {
  type SubmitErrorHandler,
  type SubmitHandler,
  useForm,
} from "react-hook-form";
import { z } from "zod";

import {
  Button,
  Combobox,
  classNames,
  Input,
  Text,
  toastError,
} from "@alpacahq/ui/old";

import { getErrorMessage, useMutationRequest } from "~/v2/api/hooks";
import { useGetAssets } from "~/v2/api/hooks/useGetAssets";
import { getCusipFromSymbol } from "~/v2/api/rest";
import {
  ACATS_ASSET_POSITIONS,
  validateAcatsAssetsByCusip,
} from "~/v2/api/rest/acats";
import {
  type AssetMap,
  createAssetMap,
  normalizeAsset,
} from "~/v2/components/common/topbar";
import type { EquityWithSymbol } from "~/v2/pages/dashboard/funding/common/types";
import { zNonEmptyString } from "../../../common/util";

const zEquityInputSchema = z.object({
  symbol: zNonEmptyString,
  qty: z
    .number({ error: "Specify shares" })
    .int({ message: "No fractional shares" })
    .min(1, { message: "Minimum 1 share" })
    .optional(),

  position: z.enum(ACATS_ASSET_POSITIONS),
});

type EquityInputSchema = z.infer<typeof zEquityInputSchema>;

export interface EquityInputProps {
  editEquity: EquityWithSymbol | null;
  onDelete: (equity?: EquityWithSymbol) => void;
  onSave: (equity: EquityWithSymbol) => void;
  isDisplayed: boolean;
}

export const EquityInput = ({
  editEquity,
  onDelete,
  onSave,
  isDisplayed,
}: EquityInputProps) => {
  const [isAllShares, setIsAllShares] = useState(true);

  const { assets: equities } = useGetAssets();

  // memoize assets into index map for fast searching
  const assetMap = useMemo<AssetMap>(() => {
    const newMap = new Map();
    equities.forEach((asset) => {
      newMap.set(asset.symbol, asset.name);
    });
    return createAssetMap(newMap);
  }, [equities]);

  const onSearch = useCallback(
    (query: string) => {
      // normalize query string for searching
      const normalized = normalizeAsset(query);

      // find and set match based on first letter
      const match = assetMap[normalized.charAt(0)];

      // if no match, set empty array
      if (!match) {
        return [];
      }

      // sort by symbol in descending alphabetic order
      const matched = Object.keys(match)
        .filter((key) => key.startsWith(normalized))
        .map((key) => match[key])
        .sort((a, b) => {
          const matchA = a.symbol?.startsWith(normalized);
          const matchB = b.symbol?.startsWith(normalized);

          // if both match, sort
          if (matchA && matchB) {
            return (a.symbol?.length || 0) - (b.symbol?.length || 0);
          }

          if (matchA) {
            // negative number means a comes first
            return -1;
          } else if (matchB) {
            // positive number means b comes first
            return 1;
          } else {
            // if neither match, return 0
            return 0;
          }
        });

      return (
        uniqBy(matched, (asset) => asset.symbol)
          // only show 10 results, very slow otherwise
          .slice(0, 10)
          // filter out assets without symbol
          .filter(({ symbol }) => !!symbol)
          // map to suggestion object
          .map(
            // cast as string because we already filtered out assets without symbol or name
            ({ symbol, name }) => ({
              symbol: symbol as string,
              description: name || "no asset description found",
            }),
            [],
          )
      );
    },
    [assetMap],
  );

  const {
    watch,
    register,
    handleSubmit,
    setValue,
    clearErrors,
    reset: formReset,
    formState: { errors },
    setError,
  } = useForm<EquityInputSchema>({
    resolver: zodResolver(
      zEquityInputSchema.superRefine((data, ctx) => {
        if (!isAllShares && !data.qty) {
          ctx.addIssue({
            path: ["qty"],
            code: z.ZodIssueCode.custom,
            message: "Specify shares",
          });
        }
      }),
    ),
    mode: "all",
    defaultValues: {
      symbol: "",
      position: "LONG",
      qty: undefined,
    },
  });

  const reset = () => {
    setValue("symbol", "");
    setValue("position", "LONG");
    setValue("qty", undefined);
    setIsAllShares(true);
  };

  const symbol = watch("symbol");
  const comboBoxValue = symbol ? { symbol, description: "" } : null;

  const onSymbolChange = (
    o: { symbol: string; description: string } | null,
  ) => {
    setValue("symbol", o?.symbol ?? "");
  };

  const onAllSharesClicked = () => {
    setIsAllShares(true);
    setValue("qty", undefined);
    clearErrors("qty");
  };

  const verifyAssetMutation = useMutationRequest(
    async (asset: EquityInputSchema) => {
      const cusipRes = await getCusipFromSymbol(asset.symbol);

      if (!cusipRes.cusip) {
        throw new Error("Cusip not found");
      }

      return validateAcatsAssetsByCusip([cusipRes.cusip]);
    },
    {
      onSuccess: (response, equity) => {
        const validationResponse = response.validation_results.find(
          (a) => a.symbol === equity.symbol,
        );

        // Asset is valid
        if (validationResponse?.is_valid) {
          onSave({
            ...equity,
            qty: equity.qty ? `${equity.qty}` : undefined,
            cusip: validationResponse.cusip,
          });
          reset();
          return;
        }

        // Asset is not valid, get reason
        const reason = validationResponse?.validation_issues?.join(", ");
        toastError(`Asset failed validation${reason ? `: ${reason}` : ""}`);
        setError("symbol", {
          message: `${reason ? `: ${reason}` : "This asset is not supported"}`,
        });
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toastError(`Failed to validate asset: ${errorMessage}`);
        setError("symbol", {
          message: "Asset failed validation",
        });
      },
    },
  );

  const onSubmitValid: SubmitHandler<EquityInputSchema> = (data) =>
    verifyAssetMutation.mutate(data);

  const onSubmitInvalid: SubmitErrorHandler<EquityInputSchema> = () => {};

  const onDeleteClicked = () => {
    reset();
    onDelete(editEquity ?? undefined);
  };

  useEffect(() => {
    if (editEquity) {
      const qty = parseInt(editEquity?.qty ?? "0", 10);
      const isQtyValid = !Number.isNaN(qty) && (qty ?? 0) > 0;

      formReset({
        symbol: editEquity.symbol,
        position: editEquity.position,
        qty: isQtyValid ? qty : undefined,
      });

      if (isQtyValid) {
        setIsAllShares(false);
      }
    }
  }, [editEquity, formReset]);

  return (
    <form
      className={classNames(
        "pointer-events-none flex max-h-0 flex-col overflow-hidden opacity-0 transition-all",
        isDisplayed &&
          "pointer-events-auto max-h-[800px] gap-4 overflow-visible opacity-100",
      )}
      onSubmit={handleSubmit(onSubmitValid, onSubmitInvalid)}
    >
      <Combobox
        label="Select Equity"
        placeholder="Search for Stocks and ETFs"
        options={onSearch}
        getOptionKey={(o) => o.symbol}
        getOptionLabel={(o) => o.symbol}
        getOptionSecondary={(o) => o.description}
        value={comboBoxValue}
        onChange={onSymbolChange}
        optionSpacing="justify-between"
        icon="MagnifyingGlass"
        variant="v2"
        status={errors.symbol?.message ? "error" : "neutral"}
        errorMsg={errors.symbol?.message}
        openOnFocus
      />
      <div>
        <fieldset className="flex flex-col gap-2">
          <legend className="font-semibold text-gray-700 text-sm">
            Specify Shares
          </legend>
          <div className="flex items-center gap-1">
            <input
              type="radio"
              id="full-amount"
              name="quantity"
              value="full-amount"
              checked={isAllShares}
              onChange={onAllSharesClicked}
              className="cursor-pointer"
            />
            <label
              htmlFor="full-amount"
              className="cursor-pointer font-medium text-sm"
            >
              Full Amount
            </label>
          </div>
          <div className="flex items-center gap-1">
            <input
              type="radio"
              id="specify-shares"
              name="quantity"
              value="specify-shares"
              checked={!isAllShares}
              onChange={() => setIsAllShares(false)}
              className="cursor-pointer"
            />
            <label
              htmlFor="specify-shares"
              className="cursor-pointer font-medium text-sm"
            >
              Specify Shares
            </label>
          </div>
        </fieldset>
        <div
          className={classNames(
            "pointer-events-none max-h-0 overflow-hidden opacity-0 transition",
            !isAllShares && "pointer-events-auto max-h-28 pt-4 opacity-100",
          )}
        >
          <Input
            label="Shares"
            placeholder="Enter number of shares"
            type="number"
            {...register("qty", {
              setValueAs: (value) => {
                if (value === "" || Number.isNaN(Number(value))) {
                  return undefined;
                }
                return Number(value);
              },
            })}
            status={errors.qty?.message ? "error" : "neutral"}
            errorMsg={errors.qty?.message}
            rightIcon={
              <Text className="font-medium text-gray-700 text-sm">Shares</Text>
            }
          />
        </div>
      </div>
      <fieldset className="flex flex-col gap-2">
        <legend className="font-semibold text-gray-700 text-sm">
          Position
        </legend>
        <div className="flex items-center gap-1">
          <input
            {...register("position")}
            type="radio"
            id="LONG"
            name="position"
            value="LONG"
            className="cursor-pointer"
          />
          <label htmlFor="LONG" className="cursor-pointer font-medium text-sm">
            Long
          </label>
        </div>
        <div className="flex items-center gap-1">
          <input
            {...register("position")}
            type="radio"
            id="SHORT"
            name="position"
            value="SHORT"
            className="cursor-pointer"
          />
          <label htmlFor="SHORT" className="cursor-pointer font-medium text-sm">
            Short
          </label>
        </div>
      </fieldset>
      <div className="flex items-center justify-between gap-2">
        <Button variant="v2/stroke" type="button" onClick={onDeleteClicked}>
          Delete
        </Button>
        <Button
          variant="v2/filled"
          type="submit"
          disabled={verifyAssetMutation.isPending}
          loading={verifyAssetMutation.isPending}
        >
          Save
        </Button>
      </div>
    </form>
  );
};
